import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { WrenchIcon as Wrench, StorefrontIcon as Storefront, CrownIcon as Crown, TruckIcon as Truck, PauseIcon as Pause, EyeSlashIcon as EyeSlash, ShieldCheckIcon as ShieldCheck } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

/**
 * Persona-aware availability tab for Settings.
 *
 * Detects which revenue-generating resources the current user owns
 * (service provider, storefront, kennel, transporter) and renders
 * one pause/visibility card per matching persona. Empty state =
 * user has none → a soft CTA prompts them to apply for a relevant
 * persona.
 *
 * Lives outside the main Settings file because it's self-contained
 * — its own data fetch (`/me/availability/summary`), its own
 * mutation endpoints per persona, and its own optimistic update
 * pattern. Keeping it isolated stops Settings.jsx from creeping
 * past 1000 lines.
 */
export default function AvailabilityTab() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const { data } = await api.get("/me/availability/summary");
      setSummary(data);
    } catch {
      setSummary({
        provider: { present: false },
        store: { present: false },
        breeder: { present: false },
        transporter: { present: false },
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  if (loading) {
    return (
      <div className="text-[#6B6B6B]" data-testid="availability-loading">Loading availability…</div>
    );
  }

  const anyPresent =
    summary.provider.present ||
    summary.store.present ||
    summary.breeder.present ||
    summary.transporter.present;

  return (
    <div className="space-y-6" data-testid="availability-tab">
      {!anyPresent && (
        <div
          className="bg-cream/40 border border-[#E2DAC2] rounded-xl p-6"
          data-testid="availability-empty-state"
        >
          <h2 className="font-spectral text-2xl tracking-tight mb-2">
            Nothing to pause yet
          </h2>
          <p className="text-sm text-[#6B6B6B] leading-relaxed max-w-2xl">
            Availability controls show up once you open a kennel, a storefront,
            apply to list as a service provider, or onboard as a transporter.
            They let you go on vacation without forfeiting search ranking — bookings
            and orders pause; existing customers stay unaffected.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button asChild variant="outline" className="rounded-full border-[#E5E5E5]">
              <Link to="/provider/apply" data-testid="apply-provider-cta">Apply to list as a pro</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-[#E5E5E5]">
              <Link to="/me/store" data-testid="open-store-cta">Open a storefront</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-[#E5E5E5]">
              <Link to="/dashboard" data-testid="open-kennel-cta">Set up your kennel</Link>
            </Button>
          </div>
        </div>
      )}

      {summary.provider.present && (
        <ProviderAvailabilityCard
          kind={summary.provider.kind}
          initial={summary.provider.availability}
          onSaved={reload}
        />
      )}
      {summary.store.present && (
        <StoreAvailabilityCard
          name={summary.store.name}
          slug={summary.store.slug}
          initial={summary.store.availability}
          onSaved={reload}
        />
      )}
      {summary.breeder.present && (
        <BreederVisibilityCard
          name={summary.breeder.name}
          slug={summary.breeder.slug}
          initial={summary.breeder.hidden_from_directory}
          onSaved={reload}
        />
      )}
      {summary.breeder.present && (
        <BreederRefundTermsCard onSaved={reload} />
      )}
      {summary.transporter.present && (
        <TransporterStatusCard
          name={summary.transporter.name}
          status={summary.transporter.status}
        />
      )}
    </div>
  );
}

// ---------- Provider --------------------------------------------------

function ProviderAvailabilityCard({ kind, initial, onSaved }) {
  const [paused, setPaused] = useState(Boolean(initial?.paused));
  const [message, setMessage] = useState(initial?.paused_message || "");
  const [until, setUntil] = useState(initial?.paused_until || "");
  const [saving, setSaving] = useState(false);
  const kindLabel = ({ trainer: "training", groomer: "grooming", walker: "walking", sitter: "sitting" })[kind] || "service";

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me/provider/availability", {
        paused,
        paused_message: message || null,
        paused_until: until || null,
      });
      toast.success(paused ? `Your ${kindLabel} bookings are paused.` : "You're back online.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PersonaCard
      icon={Wrench}
      title={`${kindLabel[0].toUpperCase() + kindLabel.slice(1)} availability`}
      blurb={`Pause new ${kindLabel} bookings without affecting confirmed appointments. Your public profile shows a paused banner with your note + return date.`}
      testId="provider-availability-card"
      paused={paused}
      onTogglePaused={setPaused}
      message={message}
      setMessage={setMessage}
      until={until}
      setUntil={setUntil}
      onSave={save}
      saving={saving}
      pauseLabel="Pause new bookings"
    />
  );
}

// ---------- Store -----------------------------------------------------

function StoreAvailabilityCard({ name, slug, initial, onSaved }) {
  const [paused, setPaused] = useState(Boolean(initial?.paused));
  const [message, setMessage] = useState(initial?.paused_message || "");
  const [until, setUntil] = useState(initial?.paused_until || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me/store/availability", {
        paused,
        paused_message: message || null,
        paused_until: until || null,
      });
      toast.success(paused ? "Your shop is on a break." : "Your shop is open again.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PersonaCard
      icon={Storefront}
      title={`${name || "Storefront"} — shop availability`}
      blurb={
        <>
          Take your shop on vacation. The public page at <span className="font-mono text-[12px]">/store/{slug}</span> will show your note instead of products, and checkout will be blocked. Existing orders are unaffected.
        </>
      }
      testId="store-availability-card"
      paused={paused}
      onTogglePaused={setPaused}
      message={message}
      setMessage={setMessage}
      until={until}
      setUntil={setUntil}
      onSave={save}
      saving={saving}
      pauseLabel="Pause checkout"
    />
  );
}

// ---------- Breeder ---------------------------------------------------

function BreederVisibilityCard({ name, slug, initial, onSaved }) {
  const [hidden, setHidden] = useState(Boolean(initial));
  const [saving, setSaving] = useState(false);

  const save = async (next) => {
    setSaving(true);
    const prev = hidden;
    setHidden(next); // optimistic
    try {
      await api.patch("/me/breeder/visibility", { hidden_from_directory: next });
      toast.success(next ? "Your kennel is hidden from the public directory." : "Your kennel is back in the directory.");
      onSaved && onSaved();
    } catch (e) {
      setHidden(prev);
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl shadow-luxury-light overflow-hidden"
      data-testid="breeder-visibility-card"
    >
      <div className="px-6 py-5 flex items-center gap-3">
        <Crown size={22} weight="duotone" className="text-gold" />
        <h2 className="font-spectral text-2xl tracking-tight">{name || "Kennel"} — directory visibility</h2>
      </div>
      <div className="gold-hairline mx-6" />
      <div className="px-6 py-5">
        <p className="text-sm text-[#6B6B6B] mb-4 max-w-2xl leading-relaxed">
          Hide your kennel from the public <Link to="/breeders" className="underline">/breeders</Link> directory while you focus on private work. Individual dog listings remain live so direct links and active conversations aren't broken.
        </p>
        <div className="flex items-start gap-4 py-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-ink flex items-center gap-2">
              <EyeSlash size={16} weight="duotone" className="text-[#6B6B6B]" />
              Hide from public directory
            </h3>
            <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">
              {slug ? <>Your public page at <span className="font-mono text-[12px]">/kennels/{slug}</span> will return 404 to logged-out visitors.</> : "Your kennel page will be hidden from anonymous visitors."}
            </p>
          </div>
          <Switch
            checked={hidden}
            onCheckedChange={save}
            disabled={saving}
            data-testid="breeder-hide-toggle"
          />
        </div>
      </div>
    </div>
  );
}

// ---------- Breeder refund-policy extensions -------------------------
//
// Path-1 venue posture establishes a 30-day default refund FLOOR.
// Breeders can only EXTEND that floor (offer more buyer protection)
// or layer a non-refundable deposit on top of it. They can never
// reduce the floor — that's what keeps the venue posture defensible.

function BreederRefundTermsCard({ onSaved }) {
  const [loaded, setLoaded] = useState(false);
  const [days, setDays] = useState(0);
  const [extensionTerms, setExtensionTerms] = useState("");
  const [depositPct, setDepositPct] = useState(0);
  const [depositTerms, setDepositTerms] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/me/breeder/refund-terms")
      .then(({ data }) => {
        if (cancelled || !data) return;
        setDays(Number(data.refund_extension_days || 0));
        setExtensionTerms(data.refund_extension_terms || "");
        setDepositPct(Number(data.non_refundable_deposit_pct || 0));
        setDepositTerms(data.deposit_terms || "");
      })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me/breeder/refund-terms", {
        refund_extension_days: Number(days) || 0,
        refund_extension_terms: extensionTerms.trim() || null,
        non_refundable_deposit_pct: Number(depositPct) || 0,
        deposit_terms: depositTerms.trim() || null,
      });
      toast.success("Refund policy extensions saved.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl shadow-luxury-light overflow-hidden"
      data-testid="breeder-refund-terms-card"
    >
      <div className="px-6 py-5 flex items-center gap-3">
        <ShieldCheck size={22} weight="duotone" className="text-emerald-700" />
        <h2 className="font-spectral text-2xl tracking-tight">Refund policy extensions</h2>
      </div>
      <div className="gold-hairline mx-6" />
      <div className="px-6 py-5 space-y-5">
        <div className="rounded-lg bg-[#FBF7EC] border border-[#E8D9A6] p-3 text-[12.5px] text-[#3B0E07] leading-relaxed">
          <strong>Platform floor:</strong> if a dispute is filed and the buyer
          and seller don&apos;t reach mutual agreement within 30 days, the
          buyer is refunded in full. You can only <em>extend</em> that
          window or layer a non-refundable deposit on top of it — never
          reduce buyer protection.
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="breeder-refund-days" className="text-xs font-bold uppercase tracking-[0.15em]">
              Extend refund window by (days)
            </Label>
            <Input
              id="breeder-refund-days"
              type="number"
              min={0}
              max={365}
              value={days}
              onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
              className="mt-1.5"
              data-testid="breeder-refund-days-input"
              disabled={!loaded}
            />
            <p className="text-[11px] text-[#9A9A9A] mt-1">
              Total = 30-day floor + this. Set to 0 to use the floor only.
            </p>
          </div>
          <div>
            <Label htmlFor="breeder-deposit-pct" className="text-xs font-bold uppercase tracking-[0.15em]">
              Non-refundable deposit (% of sale, 0–50)
            </Label>
            <Input
              id="breeder-deposit-pct"
              type="number"
              min={0}
              max={50}
              step="0.5"
              value={depositPct}
              onChange={(e) => setDepositPct(e.target.value)}
              className="mt-1.5"
              data-testid="breeder-deposit-pct-input"
              disabled={!loaded}
            />
            <p className="text-[11px] text-[#9A9A9A] mt-1">
              Capped at 50% by policy. Forfeited if buyer cancels pre-pickup.
            </p>
          </div>
        </div>

        <div>
          <Label htmlFor="breeder-refund-terms-text" className="text-xs font-bold uppercase tracking-[0.15em]">
            Refund extension terms (optional, public)
          </Label>
          <Textarea
            id="breeder-refund-terms-text"
            value={extensionTerms}
            onChange={(e) => setExtensionTerms(e.target.value.slice(0, 2000))}
            placeholder="e.g. Full refund if a licensed vet diagnoses a congenital condition within 14 days of pickup…"
            rows={3}
            className="mt-1.5"
            data-testid="breeder-refund-terms-textarea"
          />
          <p className="text-[11px] text-[#9A9A9A] mt-1">{extensionTerms.length}/2000</p>
        </div>

        <div>
          <Label htmlFor="breeder-deposit-terms-text" className="text-xs font-bold uppercase tracking-[0.15em]">
            Deposit terms (optional, public)
          </Label>
          <Textarea
            id="breeder-deposit-terms-text"
            value={depositTerms}
            onChange={(e) => setDepositTerms(e.target.value.slice(0, 2000))}
            placeholder="e.g. 20% non-refundable deposit secures pick of the litter at 4 weeks…"
            rows={3}
            className="mt-1.5"
            data-testid="breeder-deposit-terms-textarea"
          />
          <p className="text-[11px] text-[#9A9A9A] mt-1">{depositTerms.length}/2000</p>
        </div>

        <div className="flex justify-end pt-1">
          <Button
            onClick={save}
            disabled={saving || !loaded}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid="breeder-refund-terms-save"
          >
            {saving ? "Saving…" : "Save refund terms"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Transporter ----------------------------------------------

function TransporterStatusCard({ name, status }) {
  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl shadow-luxury-light overflow-hidden"
      data-testid="transporter-status-card"
    >
      <div className="px-6 py-5 flex items-center gap-3">
        <Truck size={22} weight="duotone" className="text-ink" />
        <h2 className="font-spectral text-2xl tracking-tight">{name || "Transporter"} availability</h2>
      </div>
      <div className="gold-hairline mx-6" />
      <div className="px-6 py-5">
        <p className="text-sm text-[#6B6B6B] leading-relaxed max-w-2xl">
          Transporters manage their availability from the dedicated transporter dashboard so insurance and compliance checks stay in sync.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-[12px] uppercase tracking-[0.18em] text-[#6B6B6B]">
            Current status:
          </span>
          <span
            className={`text-[12px] font-bold uppercase tracking-[0.15em] px-3 py-1 rounded-full ${
              status === "active"
                ? "bg-[#3DA56E]/10 text-[#2F8056]"
                : "bg-[#E8C07D]/15 text-[#7C5615]"
            }`}
            data-testid="transporter-status-badge"
          >
            {status || "unknown"}
          </span>
          <Button asChild variant="outline" className="rounded-full border-[#E5E5E5] h-9 px-4 ml-auto">
            <Link to="/transporter/me" data-testid="transporter-dashboard-link">Open transporter dashboard</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Shared persona card ---------------------------------------

function PersonaCard({
  icon: Icon,
  title,
  blurb,
  testId,
  paused,
  onTogglePaused,
  message,
  setMessage,
  until,
  setUntil,
  onSave,
  saving,
  pauseLabel,
}) {
  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl shadow-luxury-light overflow-hidden"
      data-testid={testId}
    >
      <div className="px-6 py-5 flex items-center gap-3">
        <Icon size={22} weight="duotone" className="text-ink" />
        <h2 className="font-spectral text-2xl tracking-tight">{title}</h2>
      </div>
      <div className="gold-hairline mx-6" />
      <div className="px-6 py-5 space-y-5">
        <p className="text-sm text-[#6B6B6B] max-w-2xl leading-relaxed">{blurb}</p>

        <div className="flex items-start gap-4 py-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-ink flex items-center gap-2">
              <Pause size={16} weight="duotone" className="text-crimson" />
              {pauseLabel}
            </h3>
            <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">
              Toggling this on saves immediately for the boolean — the message and return date below are saved with the <em>Save</em> button.
            </p>
          </div>
          <Switch
            checked={paused}
            onCheckedChange={onTogglePaused}
            data-testid={`${testId}-toggle`}
          />
        </div>

        {paused && (
          <div className="space-y-3 pt-1" data-testid={`${testId}-details`}>
            <div>
              <Label htmlFor={`${testId}-msg`} className="text-xs font-bold uppercase tracking-[0.15em]">
                Public message (optional, 280 chars max)
              </Label>
              <Textarea
                id={`${testId}-msg`}
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 280))}
                placeholder="On a family trip — back next week. Thanks for your patience!"
                className="mt-1.5"
                rows={2}
                data-testid={`${testId}-message`}
              />
              <p className="text-[11px] text-[#9A9A9A] mt-1">{message.length}/280</p>
            </div>
            <div>
              <Label htmlFor={`${testId}-until`} className="text-xs font-bold uppercase tracking-[0.15em]">
                Expected return date (optional)
              </Label>
              <Input
                id={`${testId}-until`}
                type="date"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
                className="mt-1.5 max-w-xs"
                data-testid={`${testId}-until`}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button
            onClick={onSave}
            disabled={saving}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid={`${testId}-save`}
          >
            {saving ? "Saving…" : "Save availability"}
          </Button>
        </div>
      </div>
    </div>
  );
}
